import Fuse from 'fuse.js';
import { PureComponent, ReactNode } from 'react';
import { DoctorReferralIcon } from '../rightPanel/workspace/padV2/RowIcons';
import DoctorReferralTable from '../rightPanel/workspace/padV2/DoctorReferralTable';
import DoctorReferralSearchInput, { GReferWithTrack } from './DoctorReferralSearchInput';
import { toCreateReferalData } from '../../common/ReferalDialog';
import fetchSpecialityForDoctor, { Res } from '../../../api/fetchSpecialityForDoctor';
import { generateSpecialityId } from '../../../store/utils';
import { motion } from 'framer-motion';
import createReferal, { CreateReferalData } from '../../../api/createReferal';
import { notification } from 'antd';
import { track } from '../../../tracking';
import ModalDialog from '../../common/ModalDialog';
import { getGArray } from '../../../store/idbHandler';
import { DB_TABLES } from '../../../store/dbTables';

type State = {
    specialityRes?: Res;
    referData?: CreateReferalData;
    allGenieListItems?: GReferWithTrack[];
};

type Props = {
    ptId: string;
    close: () => void;
    ptName: string;
    docid: string;
};

class DoctorReferralAllPatient extends PureComponent<Props, State> {
    searchFuse: Fuse<GReferWithTrack> | undefined = new Fuse<GReferWithTrack>([], {
        keys: ['name', 's_name'],
        threshold: 0.2,
    });
    state: State = {
        specialityRes: undefined,
        referData: undefined,
    };

    onSave = async (referData: GReferWithTrack, index?: number): Promise<void> => {
        const { ptId } = this.props;
        const { specialityRes, referData: stateReferData } = this.state;

        const selectedSpeciality = specialityRes?.speciality?.find(
            (specialty) =>
                `${specialty.name?.toLowerCase()}` == referData.doc.s_name?.toLowerCase(),
        );

        if (!ptId) {
            return;
        }

        const oldRefers = stateReferData?.doc || [];

        if (selectedSpeciality) {
            const newDocs = [...oldRefers];
            if (index !== undefined) {
                newDocs[index] = {
                    s_name: referData.doc.s_name || selectedSpeciality.s_name,
                    s_id:
                        referData.doc.s_id ||
                        (selectedSpeciality.id.toString() || referData.doc.s_name
                            ? generateSpecialityId(referData.doc.s_name || '')
                            : undefined),

                    ...referData.doc,
                };
            } else {
                newDocs.push({
                    s_name: selectedSpeciality.s_name || referData.doc.s_name,
                    s_id:
                        referData.doc.s_id ||
                        (selectedSpeciality.id.toString() || referData.doc.s_name
                            ? generateSpecialityId(referData.doc.s_name || '')
                            : undefined),

                    ...referData.doc,
                });
            }
            this.setState({
                referData: toCreateReferalData({
                    data: {
                        doc: newDocs,
                    },
                    ptId: ptId,
                }),
            });
        } else {
            const newDocs = [...oldRefers];
            if (index !== undefined) {
                newDocs[index] = {
                    ...referData.doc,

                    s_name: referData.doc.s_name,
                    s_id:
                        referData.doc.s_id ||
                        (referData.doc.s_name
                            ? generateSpecialityId(referData.doc.s_name || '')
                            : undefined),
                };
            } else {
                newDocs.push({
                    ...referData.doc,
                    s_name: referData.doc.s_name,
                    s_id:
                        referData.doc.s_id ||
                        (referData.doc.s_name
                            ? generateSpecialityId(referData.doc.s_name || '')
                            : undefined),
                });
            }

            this.setState({
                referData: toCreateReferalData({
                    data: {
                        doc: newDocs,
                    },
                    ptId: ptId,
                }),
            });
        }
    };

    onReferralSend = async () => {
        const { ptName, close } = this.props;
        const { referData } = this.state;

        if (!referData) {
            return;
        }
        try {
            const t = await createReferal(referData);

            if (!t.success) {
                notification.error({
                    message: t.message || 'failed to refer',
                    duration: 5,
                    placement: 'topRight',
                });
            } else {
                notification.success({
                    message: 'Referral successfully sent',
                    duration: 5,
                    placement: 'topRight',
                });
            }

            track('CREATE_REFERRAL', {
                channel: 'non-pad',
                success: `${t.success}`,
                referralId: t.id || '',
                name: ptName,
            });
        } catch (e) {
            console.error(e);
            notification.error({
                message: 'failed to refer',
                duration: 5,
                placement: 'topRight',
            });
        }

        close();
    };

    onSaveNotes = (index: number, notes: string): void => {
        const { referData } = this.state;

        if (!referData) {
            return;
        }

        const refers = referData?.doc || [];
        refers[index].note = notes;

        if (referData) {
            this.setState({
                referData: { ...referData },
            });
        }
    };

    async componentDidMount(): Promise<void> {
        const gReferElements = await getGArray<GReferWithTrack>(this.props.docid, DB_TABLES.refer);
        const gReferItems =
            gReferElements?.map((option) => ({
                ...option,
                name: [option?.doc?.name?.f, option?.doc?.name?.l].filter(Boolean).join(' '),
            })) || [];

        this.searchFuse = new Fuse<GReferWithTrack>(gReferItems, {
            keys: ['name', 's_name'],
            threshold: 0.2,
        });

        this.setState({
            allGenieListItems: gReferItems,
        });

        fetchSpecialityForDoctor()
            .then((res) => {
                if (res.success) {
                    this.setState({ specialityRes: res });
                } else {
                    console.error('error');
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }

    deleteReferral = (index: number): void => {
        const { referData } = this.state;
        const refers = referData?.doc || [];
        refers.splice(index, 1);

        this.setState({
            referData: {
                ...referData,
                doc: refers,
            },
        });
    };

    render(): ReactNode {
        const { specialityRes, referData, allGenieListItems } = this.state;
        const { close, docid } = this.props;

        return (
            <ModalDialog>
                <motion.div
                    initial={{ opacity: 0 }}
                    exit={undefined}
                    animate={{ opacity: 1 }}
                    style={{ zIndex: 50 }}
                    transition={{ duration: 0.2, ease: 'linear' }}
                    className="font-lato absolute select-none"
                >
                    <div onClick={close} className="modal-overlay"></div>
                    <div
                        className="modal-content w-70p h-auto flex flex-col"
                        style={{
                            bottom: '150%',
                            top: '30%',
                        }}
                    >
                        <div className="rounded-8 pt-8 px-16 pb-16 bg-white row focusable space-y-8">
                            <div>
                                <div className="flex items-center space-x-16">
                                    <div className="space-x-10 flex items-center">
                                        <div>
                                            <DoctorReferralIcon />
                                        </div>
                                        <p className="font-700 text-16 text-neutral-black">
                                            Refer to a doctor
                                        </p>
                                    </div>
                                </div>
                                <DoctorReferralTable
                                    referData={referData}
                                    deleteReferral={this.deleteReferral}
                                    onSaveNotes={this.onSaveNotes}
                                    onSelect={this.onSave}
                                    specialities={specialityRes?.speciality}
                                />
                                {!referData && (
                                    <div className="mt-8 space-y-16 w-full">
                                        <DoctorReferralSearchInput
                                            elements={[]}
                                            onSelect={this.onSave}
                                            specialities={specialityRes?.speciality}
                                            docid={docid}
                                            searchFuse={this.searchFuse}
                                            allGenieListItems={allGenieListItems}
                                        />
                                    </div>
                                )}{' '}
                            </div>
                            {referData && (
                                <div
                                    onClick={this.onReferralSend}
                                    className="flex items-center justify-end"
                                >
                                    <button className="primary-btn px-12 py-8 btn btn--small space-x-4">
                                        Send Referral
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            </ModalDialog>
        );
    }
}

export default DoctorReferralAllPatient;
